import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../constants/api";
import { showError, showSuccess } from "../../utils/toast.js";
import { useConfirm } from "../ui/ConfirmationModal.jsx";
import UserChip from "../ui/UserChip.jsx";
import { FileText, UserCheck, Building2, MapPin, ChevronDown, PencilLine } from "lucide-react";
import { formatCHPhone } from "../../utils/phone.js";
import { Z_INDEX } from "../ui/zIndex.js";
import AppButton from "../ui/AppButton.jsx";
import { PdfExportActionSheet } from "../ui/PdfExportActions.jsx";
import { Field } from "../forms/shared/FormAtoms.jsx";
import {
  detailCardCompactSecondaryLinkClassName,
  entityMoreMenuDangerItemClassName,
  entityMoreMenuItemClassName,
  entityMoreMenuItemEmojiClassName,
  entityMoreMenuPanelClassName,
  entityMoreMenuTriggerClassName,
  entityInfoBlockHeaderShellClassName,
  entityInfoBlockHeaderTitleWrapClassName,
  entityInfoBlockHeaderActionsClassName,
  entityInfoBlockEditButtonClassName,
} from "../../design/tokens.js";
import { NeutralBadge } from "../ui/TypBadge.jsx";
import { sanitizeFileUrl } from "../../utils/filePreview.js";
import EntityInlineFileLinks from "../ui/EntityInlineFileLinks.jsx";
import { buildWartungInlineFiles } from "../../utils/entityInlineFilePresets.js";
function wartungStatusLabel(value) {
  if (value === "AKTIV") return "Aktiv";
  if (value === "PAUSIERT") return "Pausiert";
  if (value === "ARCHIVIERT") return "Archiviert";
  return "Aktiv";
}
const toDateInput = v => v ? String(v).slice(0, 10) : "";
const fmtDate = v => {
  if (!v) return "–";
  const raw = String(v).trim();
  const d = /^\d{4}-\d{2}-\d{2}$/.test(raw) ? new Date(`${raw}T12:00:00`) : new Date(raw);
  if (Number.isNaN(d.getTime())) return "–";
  return d.toLocaleDateString("de-CH", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric"
  });
};
function CollapsibleSection({
  icon,
  label,
  badge,
  defaultOpen = false,
  className = "",
  children
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={className}>
      <AppButton
        type="button"
        variant="ghost"
        onClick={() => setOpen(v => !v)}
        className={`w-full flex items-center gap-1.5 py-1 text-left transition-colors rounded !justify-start !px-0 min-h-0 ${"hover:bg-white/[0.03]"}`}
      >
        {icon}
        <span className={`text-xs font-semibold uppercase tracking-wide flex-1 ${"text-blue-300/60"}`}>{label}</span>
        {badge != null && !open && <span className={`text-[10px] tabular-nums ${"text-blue-300/40"}`}>{badge}</span>}
        <ChevronDown className={`w-3 h-3 shrink-0 transition-transform ${open ? "rotate-180" : ""} ${"text-blue-300/40"}`} />
      </AppButton>
      {open && <div className="mt-1">{children}</div>}
    </div>
  );
}
function StatusInlineEdit({
  status,
  saving,
  onChange
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const close = e => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const esc = e => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", esc);
    };
  }, [open]);
  const options = [{
    value: "AKTIV",
    label: "Aktiv"
  }, {
    value: "PAUSIERT",
    label: "Pausiert"
  }, {
    value: "ARCHIVIERT",
    label: "Archiviert"
  }];
  return <span className="relative" ref={ref}>
      <AppButton type="button" variant="ghost" onClick={() => setOpen(v => !v)} disabled={saving} className={`inline-flex items-center justify-center w-5 h-5 rounded transition !p-0 ${"text-blue-300/50 hover:text-blue-200 hover:bg-white/10"} ${saving ? "opacity-40 cursor-wait" : ""}`} title="Status ändern" aria-label="Status ändern">
        <PencilLine className="w-3 h-3" />
      </AppButton>
      {open ? <div className={`absolute left-0 top-full mt-1 z-40 rounded-lg border shadow-lg overflow-hidden min-w-[130px] ${"bg-[#0b1324] border-white/10"}`}>
          {options.map(opt => <AppButton key={opt.value} type="button" variant="ghost" onClick={() => {
        onChange(opt.value);
        setOpen(false);
      }} className={`flex w-full items-center gap-2 px-3 py-1.5 text-sm transition !justify-start min-h-0 ${opt.value === status ? "bg-green-500/20 text-green-100 font-medium" : "text-blue-100/80 hover:bg-white/5"}`}>
              {opt.label}
            </AppButton>)}
        </div> : null}
    </span>;
}
export default function WartungsvertragInfoBlock({
  vertrag,
  onSaved,
  embedded = false,
  onBearbeiten,
  onCreateServiceauftrag,
  onArchive
}) {
    const confirm = useConfirm();
  const [saving, setSaving] = useState(false);
  const [savingStatus, setSavingStatus] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [vertragPdfSheet, setVertragPdfSheet] = useState(null);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({
    status: "ENTWURF",
    projektleiter_user_id: "",
    auftraggeber_user_id: "",
    assigned_user_ids: [],
    notify_days_before: 14,
    auto_renew: false,
    vertragsbeginn: "",
    laufzeit_monate: "",
    kuendigungstermin: "",
    verwaltung_text: "",
    abweichende_adresse: "",
    adresse_strasse: "",
    adresse_plz: "",
    adresse_ort: "",
    kontakt_vor_ort_name: "",
    kontakt_vor_ort_tel: "",
    kontakt_vor_ort_email: "",
    rechnung_owner_type: "BUERO",
    rechnung_owner_user_id: ""
  });
  const moreRef = useRef(null);
  useEffect(() => {
    (async () => {
      try {
        const {
          data
        } = await api.get("/service/users/options");
        setUsers(Array.isArray(data) ? data : []);
      } catch {
        setUsers([]);
      }
    })();
  }, []);
  useEffect(() => {
    if (!vertrag) return;
    setForm({
      status: ["AKTIV", "PAUSIERT", "ARCHIVIERT"].includes(String(vertrag.status || "").toUpperCase()) ? String(vertrag.status || "").toUpperCase() : "AKTIV",
      projektleiter_user_id: vertrag.projektleiter_user_id ? String(vertrag.projektleiter_user_id) : "",
      auftraggeber_user_id: vertrag.auftraggeber_user_id ? String(vertrag.auftraggeber_user_id) : "",
      assigned_user_ids: Array.isArray(vertrag.assigned_user_ids) ? vertrag.assigned_user_ids.map(Number).filter(n => n > 0) : [],
      notify_days_before: Number(vertrag.notify_days_before ?? 14),
      auto_renew: !!vertrag.auto_renew,
      vertragsbeginn: toDateInput(vertrag.vertragsbeginn),
      laufzeit_monate: vertrag.laufzeit_monate ?? "",
      kuendigungstermin: toDateInput(vertrag.kuendigungstermin),
      verwaltung_text: vertrag.verwaltung_text || "",
      abweichende_adresse: vertrag.abweichende_adresse || "",
      adresse_strasse: vertrag.adresse_strasse || "",
      adresse_plz: vertrag.adresse_plz || "",
      adresse_ort: vertrag.adresse_ort || "",
      kontakt_vor_ort_name: vertrag.kontakt_vor_ort_name || "",
      kontakt_vor_ort_tel: vertrag.kontakt_vor_ort_tel || "",
      kontakt_vor_ort_email: vertrag.kontakt_vor_ort_email || "",
      rechnung_owner_type: String(vertrag.rechnung_owner_type || "BUERO").toUpperCase() === "PL" ? "PL" : "BUERO",
      rechnung_owner_user_id: vertrag.rechnung_owner_user_id ? String(vertrag.rechnung_owner_user_id) : ""
    });
  }, [vertrag]);
  const byId = useMemo(() => {
    const map = new Map();
    (users || []).forEach(u => map.set(Number(u.id), u));
    return map;
  }, [users]);
  const kundeAdresse = [vertrag?.kunden_strasse, [vertrag?.kunden_plz, vertrag?.kunden_ort].filter(Boolean).join(" ")].filter(Boolean).join(", ");
  const kundeMapLink = kundeAdresse ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(kundeAdresse)}` : null;
  const objektAdresse = [vertrag?.objekt_adresse, [vertrag?.objekt_plz, vertrag?.objekt_ort].filter(Boolean).join(" ")].filter(Boolean).join(", ");
  const objektKurz = String(vertrag?.objekt_label || vertrag?.objekt_name || "").trim() || (objektAdresse || "");
  const objektMapLink = objektAdresse ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(objektAdresse)}` : null;
  const verwaltungKompakt = [vertrag?.kunden_firma || [vertrag?.kunden_vorname, vertrag?.kunden_nachname].filter(Boolean).join(" "), kundeAdresse, vertrag?.kunden_telefon ? `Tel: ${formatCHPhone(vertrag.kunden_telefon)}` : null, vertrag?.kunden_telefon2 ? `Tel 2: ${formatCHPhone(vertrag.kunden_telefon2)}` : null].filter(Boolean).join(", ");
  const statusValue = ["AKTIV", "PAUSIERT", "ARCHIVIERT"].includes(String(form.status || "").toUpperCase()) ? String(form.status || "").toUpperCase() : "AKTIV";
  const statusPillCls =
    statusValue === "AKTIV"
      ? "bg-green-500/20 text-green-100 border-0"
      : statusValue === "ARCHIVIERT"
        ? "bg-slate-500/20 text-slate-100 border-0"
        : "bg-amber-500/20 text-amber-100 border-0";
  const hasVerantwortungContent = Boolean(form.projektleiter_user_id || form.auftraggeber_user_id);
  const hasVerwaltungContent = Boolean(String(form.verwaltung_text || "").trim());
  const hasKontaktContent = Boolean(form.abweichende_adresse || form.adresse_strasse || form.adresse_plz || form.adresse_ort || form.kontakt_vor_ort_name || form.kontakt_vor_ort_tel || form.kontakt_vor_ort_email || vertrag?.objekt_kontakt_vor_ort_name || vertrag?.objekt_kontakt_vor_ort_tel || vertrag?.objekt_kontakt_vor_ort_email || objektAdresse);
  const hasAnyMetaSection = hasVerantwortungContent || hasVerwaltungContent || hasKontaktContent;
  async function generateContract(format) {
    if (!vertrag?.id) return;
    try {
      const {
        data
      } = await api.post(`/wartungsvertraege/${vertrag.id}/generate-contract`, {
        format,
        variant: "auswahl"
      });
      showSuccess(`Vertrag als ${String(format).toUpperCase()} generiert.`);
      const rawUrl = data?.secure_preview_url || data?.secure_download_url || data?.secure_url || "";
      const safe = sanitizeFileUrl(rawUrl);
      if (!safe) {
        showError("Datei-Link fehlt oder ist ungültig.");
      } else if (String(format).toLowerCase() === "pdf") {
        setVertragPdfSheet({
          pdfUrl: rawUrl,
          title: "Vertrag PDF",
          fileName: `wartungsvertrag-${vertrag.id}.pdf`,
        });
      } else {
        window.open(safe, "_blank", "noopener,noreferrer");
      }
      setMoreOpen(false);
      onSaved?.();
    } catch {
      showError(`Generierung (${String(format).toUpperCase()}) fehlgeschlagen.`);
    }
  }
  async function archiveVertrag() {
    if (!vertrag?.id) return;
    if (typeof onArchive === "function") {
      onArchive();
      return;
    }
    const {
      ok
    } = await confirm({
      title: "Wartungsvertrag archivieren",
      message: "Möchtest du diesen Wartungsvertrag wirklich archivieren?",
      variant: "danger",
      confirmLabel: "Archivieren"
    });
    if (!ok) return;
    try {
      setSaving(true);
      await api.put(`/wartungsvertraege/${vertrag.id}`, {
        status: "ARCHIVIERT"
      });
      showSuccess("Wartungsvertrag archiviert.");
      onSaved?.();
    } catch {
      showError("Archivieren fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  }
  async function updateStatus(nextStatus) {
    if (!vertrag?.id) return;
    const next = ["AKTIV", "PAUSIERT", "ARCHIVIERT"].includes(String(nextStatus || "").toUpperCase()) ? String(nextStatus || "").toUpperCase() : "AKTIV";
    const prev = ["AKTIV", "PAUSIERT", "ARCHIVIERT"].includes(String(form.status || "").toUpperCase()) ? String(form.status || "").toUpperCase() : "AKTIV";
    if (next === prev) return;
    try {
      setSavingStatus(true);
      await api.put(`/wartungsvertraege/${vertrag.id}`, {
        status: next
      });
      setForm(p => ({
        ...p,
        status: next
      }));
      showSuccess("Status aktualisiert.");
      onSaved?.();
    } catch {
      showError("Status konnte nicht gespeichert werden.");
    } finally {
      setSavingStatus(false);
    }
  }
  useEffect(() => {
    const onDoc = e => {
      if (moreOpen && moreRef.current && !moreRef.current.contains(e.target)) setMoreOpen(false);
    };
    const onEsc = e => {
      if (e.key === "Escape") setMoreOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onEsc);
    };
  }, [moreOpen]);
  if (!vertrag) return null;
  const containerClass = embedded ? "" : "rounded-2xl px-3 py-3 border border-default bg-surface text-primary";
  return <section className={containerClass}>
      {!embedded ? (
        <div className={entityInfoBlockHeaderShellClassName}>
          <div className={entityInfoBlockHeaderTitleWrapClassName}>
            <h2 className={`text-lg sm:text-xl font-semibold flex items-start gap-2 min-w-0 max-md:text-base max-md:leading-snug ${"text-blue-100/90"}`}>
              <FileText className="w-5 h-5 shrink-0 mt-0.5 max-md:w-4 max-md:h-4" />
              <span className="min-w-0 line-clamp-2">Wartungsinformation</span>
            </h2>
          </div>
          <div className={entityInfoBlockHeaderActionsClassName}>
            <AppButton type="button" variant="primary" size="sm" className={entityInfoBlockEditButtonClassName} title="Bearbeiten" aria-label="Bearbeiten" onClick={() => onBearbeiten?.(vertrag)}>
              Bearbeiten
            </AppButton>
            <div className="relative shrink-0" ref={moreRef}>
              <AppButton
                type="button"
                variant="secondary"
                size="sm"
                className={entityMoreMenuTriggerClassName}
                onClick={() => setMoreOpen((v) => !v)}
                aria-haspopup="menu"
                aria-expanded={moreOpen ? "true" : "false"}
                aria-label="Mehr Aktionen"
                title="Mehr Aktionen"
              >
                <span className="text-lg leading-none font-semibold px-1 tracking-wide select-none" aria-hidden>
                  ...
                </span>
              </AppButton>
              {moreOpen ? (
                <div
                  role="menu"
                  aria-label="Mehr Aktionen"
                  className={entityMoreMenuPanelClassName}
                  style={{
                    zIndex: Z_INDEX.selectPortal,
                  }}
                >
                  <AppButton type="button" role="menuitem" className={entityMoreMenuItemClassName} onClick={() => generateContract("pdf")} variant="ghost" size="sm">
                    Vertrag PDF <span className={entityMoreMenuItemEmojiClassName} aria-hidden>📄</span>
                  </AppButton>
                  <AppButton
                    type="button"
                    role="menuitem"
                    className={entityMoreMenuItemClassName}
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setMoreOpen(false);
                      onCreateServiceauftrag?.();
                    }}
                  >
                    Serviceauftrag erstellen <span className={entityMoreMenuItemEmojiClassName} aria-hidden>🛠</span>
                  </AppButton>
                  <AppButton
                    type="button"
                    role="menuitem"
                    className={entityMoreMenuDangerItemClassName}
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setMoreOpen(false);
                      archiveVertrag();
                    }}
                  >
                    Löschen <span className={entityMoreMenuItemEmojiClassName} aria-hidden>🗑</span>
                  </AppButton>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}

      <div className={`grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4 text-sm ${"text-blue-100/90"}`}>
        <div className="space-y-2.5">
          <Field isDark={true} label="Titel" value={(vertrag?.titel || "").trim() || `Wartungsvertrag #${vertrag.id} · ${vertrag.typ || "–"}`} />
          <div className="grid grid-cols-2 gap-3">
            <Field isDark={true} label="Vertragsbeginn" value={fmtDate(form.vertragsbeginn)} />
            <Field isDark={true} label="Kündigungstermin" value={fmtDate(form.kuendigungstermin)} />
          </div>
          <Field isDark={true} label="Beschreibung" value={vertrag?.notizen_intern || "–"} />
          {vertrag?.id ? (
            <EntityInlineFileLinks
              {...(buildWartungInlineFiles(vertrag.id) || {})}
              documentsSectionLabel="Wartungsdokumente"
            />
          ) : null}
        </div>
        <div className="space-y-1.5">
          <div className={"text-xs uppercase tracking-wide text-blue-300/60"}>Kunde</div>
          <div className="leading-5">
            <div className="font-medium">{vertrag.kunden_firma || "–"}</div>
            <div>{[vertrag.kunden_vorname, vertrag.kunden_nachname].filter(Boolean).join(" ") || "–"}</div>
            <div>{vertrag.kunden_strasse || "–"}</div>
            <div>{[vertrag.kunden_plz, vertrag.kunden_ort].filter(Boolean).join(" ") || "–"}</div>
          </div>
          {(vertrag.kunden_telefon || vertrag.kunden_telefon2) && (
            <div className={`text-[13px] space-y-0.5 ${"text-blue-200/80"}`}>
              {vertrag.kunden_telefon && (
                <div>
                  <span className="opacity-80">Tel:</span>{" "}
                  <a
                    href={`tel:${String(vertrag.kunden_telefon).replace(/[^\d+]/g, "")}`}
                    className={"underline underline-offset-2 hover:text-blue-100 tabular-nums"}
                  >
                    {formatCHPhone(vertrag.kunden_telefon)}
                  </a>
                </div>
              )}
              {vertrag.kunden_telefon2 && (
                <div>
                  <span className="opacity-80">Tel 2:</span>{" "}
                  <a
                    href={`tel:${String(vertrag.kunden_telefon2).replace(/[^\d+]/g, "")}`}
                    className={"underline underline-offset-2 hover:text-blue-100 tabular-nums"}
                  >
                    {formatCHPhone(vertrag.kunden_telefon2)}
                  </a>
                </div>
              )}
            </div>
          )}
          {(vertrag?.kunde_id || kundeMapLink) ? (
            <div className="flex flex-wrap items-center gap-1.5">
              {vertrag?.kunde_id ? (
                <Link to={`/kunden/${vertrag.kunde_id}`} className={detailCardCompactSecondaryLinkClassName}>
                  Öffnen
                </Link>
              ) : null}
              {kundeMapLink ? (
                <a
                  href={kundeMapLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={detailCardCompactSecondaryLinkClassName}
                >
                  Route
                </a>
              ) : null}
            </div>
          ) : null}
          <div className={`pt-1 ${"border-t border-white/10"}`} />
          <div className={"text-xs uppercase tracking-wide text-blue-300/60"}>Liegenschaft</div>
          <div className="leading-5">
            <div className="font-medium">{objektKurz ? objektKurz : vertrag?.objekt_id ? `Objekt #${vertrag.objekt_id}` : "–"}</div>
            <div>{objektAdresse || "–"}</div>
          </div>
          {(vertrag?.objekt_id || objektMapLink) && (
            <div className="flex flex-wrap items-center gap-1.5">
              {vertrag?.objekt_id ? (
                <Link to={`/objekte/${vertrag.objekt_id}`} className={detailCardCompactSecondaryLinkClassName}>
                  Öffnen
                </Link>
              ) : null}
              {objektMapLink ? (
                <a href={objektMapLink} target="_blank" rel="noopener noreferrer" className={detailCardCompactSecondaryLinkClassName}>
                  Route
                </a>
              ) : null}
            </div>
          )}
          <div className="mt-1.5 space-y-1.5 min-w-0">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
              {vertrag?.typ ? (
                <div className="flex flex-wrap items-center gap-2">
                  <span className={"text-xs uppercase tracking-wide text-blue-300/60"}>Typ</span>
                  <NeutralBadge small text={String(vertrag.typ).toUpperCase()} className="shrink-0 !border-white/[0.09]" />
                </div>
              ) : null}
              <div className="flex flex-wrap items-center gap-2 min-w-0">
                <span className={"text-xs uppercase tracking-wide text-blue-300/60"}>Status</span>
                <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium shrink-0 ${statusPillCls}`}>{wartungStatusLabel(statusValue)}</span>
                <StatusInlineEdit status={statusValue} saving={savingStatus} onChange={updateStatus} />
              </div>
              <div className="flex flex-wrap items-center gap-2 min-w-0">
                <span className={"text-xs uppercase tracking-wide text-blue-300/60 whitespace-nowrap"}>Laufzeit</span>
                <span className="text-sm text-blue-100/90 tabular-nums">{form.laufzeit_monate ? `${form.laufzeit_monate} M.` : "–"}</span>
              </div>
            </div>
            {statusValue === "PAUSIERT" ? (
              <p className={`text-xs leading-snug border-l-2 border-amber-400/40 pl-2 ${"text-amber-300/90"}`}>
                Hinweis: Automatik pausiert (Service + Benachrichtigungen).
              </p>
            ) : null}
          </div>
        </div>
      </div>

      {hasAnyMetaSection ? (
        <div className={`mt-2 pt-1.5 border-t text-sm grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-0.5 ${"border-white/10"}`}>
          {hasVerantwortungContent ? (
            <CollapsibleSection icon={<UserCheck className="w-3.5 h-3.5 opacity-60" />} label="Verantwortung" defaultOpen={false}>
              <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-[13px]">
                {form.projektleiter_user_id ? <>
                    <span className={"text-blue-300/60"}>Projektleiter:</span>
                    <span className="min-w-0">
                      <UserChip user={{
                    ...(byId.get(Number(form.projektleiter_user_id)) || {}),
                    name: byId.get(Number(form.projektleiter_user_id))?.name || "–"
                  }} variant="table" size="sm" />
                    </span>
                  </> : null}
                {form.auftraggeber_user_id ? <>
                    <span className={"text-blue-300/60"}>Auftraggeber:</span>
                    <span className="min-w-0">
                      <UserChip user={{
                    ...(byId.get(Number(form.auftraggeber_user_id)) || {}),
                    name: byId.get(Number(form.auftraggeber_user_id))?.name || "–"
                  }} variant="table" size="sm" />
                    </span>
                  </> : null}
              </div>
            </CollapsibleSection>
          ) : null}

          {hasVerwaltungContent ? (
            <CollapsibleSection icon={<Building2 className="w-3.5 h-3.5 opacity-60" />} label="Verwaltung" defaultOpen={false}>
              <div className="space-y-2">
                <div className="text-[13px] opacity-90 whitespace-pre-wrap">{form.verwaltung_text}</div>
                {verwaltungKompakt ? <div className="text-[12px] opacity-70">{verwaltungKompakt}</div> : null}
              </div>
            </CollapsibleSection>
          ) : null}

          {hasKontaktContent ? (
            <CollapsibleSection icon={<MapPin className="w-3.5 h-3.5 opacity-60" />} label="Kontakt vor Ort / Einsatzort" defaultOpen={false}>
              <div className="space-y-2">
                <div className="text-[13px] space-y-1">
                  {form.abweichende_adresse ? <div>{form.abweichende_adresse}</div> : null}
                  {form.adresse_strasse || form.adresse_plz || form.adresse_ort ? <div>{[form.adresse_strasse, [form.adresse_plz, form.adresse_ort].filter(Boolean).join(" ")].filter(Boolean).join(", ")}</div> : null}
                  {form.kontakt_vor_ort_name ? <div>Kontakt: {form.kontakt_vor_ort_name}</div> : null}
                  {form.kontakt_vor_ort_tel ? <div>Tel: {formatCHPhone(form.kontakt_vor_ort_tel)}</div> : null}
                  {form.kontakt_vor_ort_email ? <div>E-Mail: {form.kontakt_vor_ort_email}</div> : null}
                  {!form.abweichende_adresse && !(form.adresse_strasse || form.adresse_plz || form.adresse_ort) && objektAdresse ? <div>{objektAdresse}</div> : null}
                  {!form.kontakt_vor_ort_name && vertrag?.objekt_kontakt_vor_ort_name ? <div>Kontakt: {vertrag.objekt_kontakt_vor_ort_name}</div> : null}
                  {!form.kontakt_vor_ort_tel && vertrag?.objekt_kontakt_vor_ort_tel ? <div>Tel: {formatCHPhone(vertrag.objekt_kontakt_vor_ort_tel)}</div> : null}
                  {!form.kontakt_vor_ort_email && vertrag?.objekt_kontakt_vor_ort_email ? <div>E-Mail: {vertrag.objekt_kontakt_vor_ort_email}</div> : null}
                  {objektMapLink ? (
                    <div className="flex flex-wrap items-center gap-1.5">
                      <a href={objektMapLink} target="_blank" rel="noopener noreferrer" className={detailCardCompactSecondaryLinkClassName}>
                        Route
                      </a>
                    </div>
                  ) : null}
                </div>
              </div>
            </CollapsibleSection>
          ) : null}
        </div>
      ) : null}
      <PdfExportActionSheet
        open={Boolean(vertragPdfSheet)}
        onClose={() => setVertragPdfSheet(null)}
        pdfUrl={vertragPdfSheet?.pdfUrl}
        title={vertragPdfSheet?.title || "Vertrag PDF"}
        fileName={vertragPdfSheet?.fileName || "vertrag.pdf"}
      />
    </section>;
}